import { http } from '@pendo/http';

export function convertDataUriToBlob (dataURI) {
    if (dataURI instanceof Blob) {
        return dataURI;
    }
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
        byteString = atob(dataURI.split(',')[1]);
    } else {
        byteString = unescape(dataURI.split(',')[1]);
    }

    const mimeString = dataURI
        .split(',')[0]
        .split(':')[1]
        .split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
}

export function resizeImage (imgObject, targetWidth, targetHeight) {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    context.drawImage(imgObject, 0, 0, canvas.width, canvas.height);

    return {
        uri: canvas.toDataURL(),
        width: targetWidth,
        height: targetHeight,
    };
}

export function readImageFile (
    file,
    {
        autoResize = false,
        limitSize = false,
        maxDimensions = { width: 250, height: 250 },
        minDimensions = { width: 24, height: 24 },
    } = {},
) {
    const reader = new FileReader();
    let imagePromise;
    try {
        imagePromise = new Promise((resolve, reject) => {
            reader.onloadend = () => {
                const img = new Image();

                img.onload = () => {
                    const { width, height } = img;

                    if (limitSize || autoResize) {
                        const { width: minW, height: minH } = minDimensions;
                        const { width: maxW, height: maxH } = maxDimensions;

                        if (width < minW || height < minH) {
                            reject(new Error(`Image dimensions must be at least ${minW}x${minH} pixels.`));
                        }

                        if (width > maxW || height > maxH) {
                            if (autoResize) {
                                resolve(resizeImage(img, maxW, maxH));
                            } else {
                                reject(new Error(`Image dimensions must not exceed ${maxW}x${maxH} pixels.`));
                            }
                        }
                    }

                    resolve({
                        uri: reader.result,
                        width,
                        height,
                    });
                };

                img.src = reader.result;
            };
        });

        const blob = convertDataUriToBlob(file);
        reader.readAsDataURL(blob);
    } catch (err) {
        return Promise.reject(new Error(err));
    }

    return imagePromise;
}

export async function createMedia (appId, fileName, params = {}) {
    params.objectName = fileName;
    const response = await http.post(`/api/s/_SID_/application/${appId}/media`, {}, { params });
    const { data } = response;

    return {
        downloadUrl: data.url,
        uploadUrl: data.uploadUrl,
        extraData: data.formData,
    };
}

export function postToGCS (url, file, data) {
    const fd = new FormData();
    Object.keys(data).forEach((key) => {
        fd.append(key, data[key]);
    });
    fd.append('file', file); // must be the last entry

    return http.post(url, fd, {
        withCredentials: false,
        headers: {
            'X-Pendo-Impersonate': undefined,
            'Content-Type': undefined,
        },
    });
}

export async function uploadImage (name, imgDataUri, appId) {
    const urlInfo = await createMedia(appId, name);
    await postToGCS(urlInfo.uploadUrl, convertDataUriToBlob(imgDataUri), urlInfo.extraData);

    return urlInfo.downloadUrl;
}

export async function uploadImageFile (file, appId, fileSizeLimits = {}) {
    const imgResult = await readImageFile(file, fileSizeLimits);

    return uploadImage(file.name, imgResult.uri, appId);
}

export function looksLikeSvgContent (text) {
    if (typeof text !== 'string' || !text) {
        return false;
    }

    let normalizedText = text;
    if (text.indexOf('data:') === 0) {
        const [, payload = ''] = text.split(',');
        normalizedText = text.includes(';base64,') ? atob(payload) : decodeURIComponent(payload);
    }

    return /<svg[\s>]/i.test(normalizedText);
}

function applySvgImageDimensions (img, dataUrl, width, height) {
    if (typeof dataUrl !== 'string' || dataUrl.indexOf('data:') !== 0) {
        return dataUrl;
    }

    try {
        if (!looksLikeSvgContent(dataUrl)) {
            return dataUrl;
        }
    } catch {
        return dataUrl;
    }

    img.width = `${width}`;
    img.height = `${height}`;

    if (dataUrl.indexOf('data:image/svg') === 0) {
        return dataUrl;
    }

    const [, base64Payload = ''] = dataUrl.split(';base64,');

    if (!base64Payload) {
        return dataUrl;
    }

    return `data:image/svg+xml;base64,${base64Payload}`;
}

const IMAGE_TYPE_PNG = 'image/png';
const IMAGE_TYPE_JPEG = 'image/jpeg';
const IMAGE_TYPE_SVG = 'image/svg+xml';

const EXTENSION_IMAGE_TYPES = {
    png: IMAGE_TYPE_PNG,
    jpeg: IMAGE_TYPE_JPEG,
    jpg: IMAGE_TYPE_JPEG,
    svg: IMAGE_TYPE_SVG,
};

function getImageFileTypeFromExtension (fileName) {
    const extensionMatch = /\.([^.]+)$/i.exec(fileName);
    const extension = extensionMatch ? extensionMatch[1] : '';

    if (!extension) {
        return '';
    }

    return EXTENSION_IMAGE_TYPES[extension.toLowerCase()] || '';
}

export function getImageFileType (imageFile) {
    if (!imageFile) {
        return '';
    }

    return getImageFileTypeFromExtension(imageFile.name || '');
}

export const RASTER_IMAGE_TYPES = [IMAGE_TYPE_JPEG, IMAGE_TYPE_PNG];

export const PORTAL_IMAGE_TYPES = [...RASTER_IMAGE_TYPES, IMAGE_TYPE_SVG];

export function convertImage (imgSrc, props) {
    const { width, height } = props;

    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = function () {
            try {
                const resizedResult = resizeImage(img, width, height);
                resolve(resizedResult.uri); // Return the data URL directly
            } catch (error) {
                reject(error);
            }
        };

        img.onerror = function () {
            reject(new Error('Failed to load image'));
        };

        if (imgSrc.name != null) {
            // imgSrc is a File object
            const reader = new FileReader();
            reader.onloadend = function () {
                const { result } = reader;
                if (typeof result === 'string') {
                    img.src = applySvgImageDimensions(img, result, width, height);
                } else {
                    img.src = result;
                }
            };
            reader.onerror = function () {
                reject(new Error('Failed to read file'));
            };
            reader.readAsDataURL(imgSrc);
        } else {
            // imgSrc is a string
            img.src = applySvgImageDimensions(img, imgSrc, width, height);
        }
    });
}

export async function convertSvgFileToPngFile (imageFile, { width = 100, height = 100 } = {}) {
    const dataUrl = await convertImage(imageFile, { width, height });
    const pngFileName = (imageFile.name || 'logo.svg').replace(/\.svg$/i, '.png');

    return new File([convertDataUriToBlob(dataUrl)], pngFileName, { type: 'image/png' });
}
